/**
 * Manual-replay signal (Phase B).
 *
 * When the user taps the "Play (AI voice)" button to REPLAY a coach message
 * while voice mode is on, the mic is already listening for their next turn — so
 * it would capture the replayed coach voice and auto-send it. This tiny channel
 * lets the replay button announce "a manual replay is playing" so the voice loop
 * can pause listening for its duration and resume when it ends.
 *
 * Deliberately SEPARATE from `coach-playback` (which drives barge-in): this only
 * concerns the manual replay button, so pausing/resuming the mic here never
 * entangles with the barge-in VAD.
 */
type Listener = (active: boolean) => void;

const listeners = new Set<Listener>();
let active = false;

/** Subscribe to manual-replay start/stop. Returns an unsubscribe function. */
export function subscribeManualReplay(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The user started a manual replay of a coach clip. */
export function markManualReplayStart(): void {
  if (active) return;
  active = true;
  listeners.forEach((l) => l(true));
}

/** The manual replay stopped, finished, or failed. */
export function markManualReplayEnd(): void {
  if (!active) return;
  active = false;
  listeners.forEach((l) => l(false));
}

export function isManualReplayActive(): boolean {
  return active;
}
